import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Logo } from '@careandkey/shared';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/auth';

// Shown when an invite / recovery link is being used: the invited user sets
// their own password (via supabase.auth.updateUser), then lands in the app.
// We wait for the client to establish the recovery session from the URL
// (detectSessionInUrl) before allowing submit — calling setSession ourselves
// would race that and fail with "Auth session missing".
export function SetPassword() {
  const { t } = useTranslation();
  const { session, clearRecovery } = useAuth();
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!session) { setError(t('auth.preparing')); return; }
    if (pw.length < 8) { setError(t('auth.pwTooShort')); return; }
    if (pw !== pw2) { setError(t('auth.pwMismatch')); return; }
    setBusy(true); setError(null);
    const { error } = await supabase.auth.updateUser({ password: pw });
    if (error) { setBusy(false); setError(error.message); return; }
    // If this set-password came from a Care & Key invitation (?token=...), finalize
    // acceptance now that the user is authenticated: creates their account (app_role
    // + scope) and routes them to the right app. No secret involved (invitee's own JWT).
    const token = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('token') : null;
    if (token) {
      const acc = await supabase.rpc('accept_invitation', { p_token: token });
      if (!acc.error && acc.data?.app_key && acc.data.app_key !== 'web') {
        const base = acc.data.app_key === 'field' ? ((import.meta as any).env?.VITE_FIELD_URL ?? '') : ((import.meta as any).env?.VITE_PORTAL_URL ?? '');
        if (base) { window.location.href = base; return; }
      }
    }
    setBusy(false);
    // clear the hash so a refresh doesn't re-trigger recovery, then enter the app
    if (typeof window !== 'undefined') window.history.replaceState(null, '', window.location.pathname);
    clearRecovery();
  }

  const field = 'h-10 w-full rounded-md border border-border bg-[#F7F8FA] px-3 text-fg outline-none focus-visible:shadow-focus';

  return (
    <div className="grid min-h-screen place-items-center bg-bg px-4">
      <div className="w-full max-w-[380px] rounded-card bg-card p-7 shadow-card">
        <div className="mb-6"><Logo variant="light" size={30} /></div>
        <h1 className="mb-1 text-lg font-semibold">{t('auth.setPasswordTitle')}</h1>
        <p className="mb-5 text-sm text-muted">{t('auth.setPasswordHint')}</p>
        <form onSubmit={submit} className="flex flex-col gap-3">
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="text-muted">{t('auth.newPassword')}</span>
            <input className={field} type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} required />
          </label>
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="text-muted">{t('auth.confirmPassword')}</span>
            <input className={field} type="password" autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} required />
          </label>
          {error && <div className="rounded-md bg-st-overdue-bg px-3 py-2 text-sm text-st-overdue-fg">{error}</div>}
          <Button type="submit" disabled={busy || !session} className="mt-1 w-full">
            {!session ? t('auth.preparing') : busy ? t('auth.signingIn') : t('auth.setPasswordSubmit')}
          </Button>
        </form>
      </div>
    </div>
  );
}
